import { ChangeDetectionStrategy, Component, computed, input, TemplateRef } from '@angular/core';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { NgTemplateOutlet } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagIcon } from '../icon/icon';
import type { BulkJobResult, BulkPreviewBucket, BulkResultStatus } from './bulk-edit.models';

interface ResultGroup {
  key: string;
  labelKey: string;
  icon: string;
  rows: BulkJobResult[];
}

const STATUS_ORDER: BulkResultStatus[] = ['failed', 'pending', 'succeeded', 'skipped-already-assigned', 'cancelled'];

const STATUS_LABEL_KEYS: Record<BulkResultStatus, string> = {
  failed: 'bulkEdit.status.failed',
  pending: 'bulkEdit.status.pending',
  succeeded: 'bulkEdit.status.succeeded',
  'skipped-already-assigned': 'bulkEdit.status.skippedAlreadyAssigned',
  cancelled: 'bulkEdit.status.cancelled',
};

const STATUS_ICONS: Record<BulkResultStatus, string> = {
  failed: 'error',
  pending: 'hourglass-empty',
  succeeded: 'check-circle',
  'skipped-already-assigned': 'skip-next',
  cancelled: 'cancel',
};

const BUCKET_ORDER: BulkPreviewBucket[] = ['conflictOpen', 'new', 'alreadyAssigned', 'historicalOnly'];

const BUCKET_LABEL_KEYS: Record<BulkPreviewBucket, string> = {
  new: 'bulkEdit.buckets.new',
  alreadyAssigned: 'bulkEdit.buckets.alreadyAssigned',
  conflictOpen: 'bulkEdit.buckets.conflictOpen',
  historicalOnly: 'bulkEdit.buckets.historicalOnly',
};

const BUCKET_ICONS: Record<BulkPreviewBucket, string> = {
  new: 'add-circle',
  alreadyAssigned: 'check-circle',
  conflictOpen: 'error',
  historicalOnly: 'history',
};

@Component({
  selector: 'zag-bulk-edit-job-result-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ScrollingModule, NgTemplateOutlet, TranslatePipe, ZagIcon],
  template: `
    @if (results().length === 0) {
      <p class="zag-bulk-result-list__empty">{{ 'bulkEdit.noResults' | translate }}</p>
    } @else {
      <cdk-virtual-scroll-viewport
        [itemSize]="itemSize()"
        [style.--zag-bulk-result-item-size]="itemSize() + 'px'"
        class="zag-bulk-result-list__viewport"
      >
        <ng-container *cdkVirtualFor="let row of flatRows(); trackBy: trackById">
          @if (isGroupHeader(row)) {
            <div class="zag-bulk-result-list__group-header">
              <span [zagIcon]="row.icon"></span>
              {{ row.labelKey | translate }}
              <span class="zag-bulk-result-list__group-count">({{ row.count }})</span>
            </div>
          } @else {
            <div class="zag-bulk-result-list__row" [class]="'zag-bulk-result-list__row--' + row.status">
              @if (rowTemplate() && entityResolver()?.(row.entityId); as entity) {
                <ng-container *ngTemplateOutlet="rowTemplate()!; context: { $implicit: entity }" />
              } @else if (rowTemplate()) {
                <ng-container *ngTemplateOutlet="rowTemplate()!; context: { $implicit: row }" />
              } @else {
                <span class="zag-bulk-result-list__entity-id">{{ entityNameResolver()?.(row.entityId) ?? row.entityId }}</span>
                @if (!groupByBucket() && row.bucket) {
                  <span class="zag-bulk-result-list__bucket">{{ getBucketLabel(row.bucket) | translate }}</span>
                }
                @if (row.reason) {
                  <span class="zag-bulk-result-list__reason">{{ row.reason }}</span>
                }
              }
            </div>
          }
        </ng-container>
      </cdk-virtual-scroll-viewport>
    }
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        min-height: 0;
      }

      .zag-bulk-result-list__viewport {
        flex: 1;
        width: 100%;
        min-height: 0;
      }

      .zag-bulk-result-list__empty {
        color: var(--text-color-secondary);
        font-size: var(--font-size-sm);
        text-align: center;
        padding: var(--spacing-lg);
      }

      .zag-bulk-result-list__group-header {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        box-sizing: border-box;
        height: var(--zag-bulk-result-item-size, calc(var(--spacing-xl) * 2));
        padding: var(--spacing-xs) var(--spacing-md);
        overflow: hidden;
        background: var(--surface-color-primary);
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-semibold);
        color: var(--text-color-secondary);
        position: sticky;
        top: 0;
        z-index: 1;
      }

      .zag-bulk-result-list__group-count {
        color: var(--text-color-muted);
      }

      .zag-bulk-result-list__row {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        box-sizing: border-box;
        height: var(--zag-bulk-result-item-size, calc(var(--spacing-xl) * 2));
        min-height: 0;
        padding: 0 var(--spacing-md);
        border-bottom: var(--border-width-default) solid var(--border-color-default);

        > * {
          flex: 1;
          min-width: 0;
        }
      }

      .zag-bulk-result-list__entity-id {
        font-size: var(--font-size-sm);
        color: var(--text-color-primary);
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .zag-bulk-result-list__reason {
        font-size: var(--font-size-xs);
        color: var(--color-error, var(--text-color-muted));
      }

      .zag-bulk-result-list__bucket {
        font-size: var(--font-size-xs);
        color: var(--text-color-secondary);
      }
    `,
  ],
})
export class BulkEditJobResultListComponent {
  readonly results = input.required<BulkJobResult[]>();
  readonly rowTemplate = input<TemplateRef<{ $implicit: unknown }> | undefined>(undefined);
  readonly itemSize = input(64);
  readonly entityNameResolver = input<((id: string) => string | undefined) | undefined>(undefined);
  readonly entityResolver = input<((id: string) => unknown | undefined) | undefined>(undefined);
  readonly groupByBucket = input(false);

  readonly groups = computed<ResultGroup[]>(() =>
    this.groupByBucket() ? this.groupByBucketKey(this.results()) : this.groupByStatusKey(this.results())
  );

  readonly flatRows = computed(() => {
    const rows: Array<{ type: 'header'; key: string; labelKey: string; icon: string; count: number } | (BulkJobResult & { type: 'row' })> =
      [];
    for (const group of this.groups()) {
      rows.push({ type: 'header', key: group.key, labelKey: group.labelKey, icon: group.icon, count: group.rows.length });
      for (const row of group.rows) {
        rows.push({ ...row, type: 'row' });
      }
    }
    return rows;
  });

  protected trackById = (_index: number, row: { type: string; key?: string; id?: string; entityId?: string; status?: string }): string =>
    row.type === 'header' ? `header:${row.key ?? _index}` : (row.id ?? row.entityId ?? row.status ?? String(_index));

  protected isGroupHeader(row: { type: string }): row is { type: 'header'; key: string; labelKey: string; icon: string; count: number } {
    return row.type === 'header';
  }

  protected getBucketLabel(bucket: BulkPreviewBucket): string {
    return `bulkEdit.buckets.${bucket}`;
  }

  private groupByStatusKey(results: BulkJobResult[]): ResultGroup[] {
    const byStatus = new Map<BulkResultStatus, BulkJobResult[]>();
    for (const row of results) {
      const list = byStatus.get(row.status) ?? [];
      list.push(row);
      byStatus.set(row.status, list);
    }
    return STATUS_ORDER.filter(s => byStatus.has(s)).map(s => ({
      key: s,
      labelKey: STATUS_LABEL_KEYS[s],
      icon: STATUS_ICONS[s],
      rows: byStatus.get(s) ?? [],
    }));
  }

  private groupByBucketKey(results: BulkJobResult[]): ResultGroup[] {
    const byBucket = new Map<BulkPreviewBucket, BulkJobResult[]>();
    for (const row of results) {
      const bucket = row.bucket;
      if (!bucket) continue;
      const list = byBucket.get(bucket) ?? [];
      list.push(row);
      byBucket.set(bucket, list);
    }
    return BUCKET_ORDER.filter(b => byBucket.has(b)).map(b => ({
      key: b,
      labelKey: BUCKET_LABEL_KEYS[b],
      icon: BUCKET_ICONS[b],
      rows: byBucket.get(b) ?? [],
    }));
  }
}
